import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  isDark: boolean;
  isAuto: boolean;
  setAuto: (auto: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = 'theme';
const AUTO_KEY = 'themeAuto';

/** Dark between 8 PM and 8 AM */
function getScheduledTheme(): Theme {
  const hour = new Date().getHours();
  return hour >= 20 || hour < 8 ? 'dark' : 'light';
}

/** Milliseconds until the next 8 AM or 8 PM boundary */
function msUntilNextSwitch(): number {
  const now = new Date();
  const hour = now.getHours();
  const next = new Date(now);
  next.setMinutes(0, 0, 0);

  if (hour < 8) {
    next.setHours(8);
  } else if (hour < 20) {
    next.setHours(20);
  } else {
    // after 8 PM — next switch is 8 AM tomorrow
    next.setDate(next.getDate() + 1);
    next.setHours(8);
  }

  return next.getTime() - now.getTime();
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuto, setAutoState] = useState<boolean>(() => {
    const stored = localStorage.getItem(AUTO_KEY);
    // Default to auto ON for new users
    return stored === null ? true : stored === 'true';
  });

  const [theme, setThemeState] = useState<Theme>(() => {
    if (isAuto) return getScheduledTheme();
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return 'light';
  });

  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Schedule auto-switch at 8 AM / 8 PM
  const scheduleSwitch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const ms = msUntilNextSwitch();
    timerRef.current = setTimeout(() => {
      setThemeState(getScheduledTheme());
      // Re-schedule for the next boundary
      scheduleSwitch();
    }, ms);
  }, []);

  // When auto mode changes, start/stop scheduling
  useEffect(() => {
    localStorage.setItem(AUTO_KEY, String(isAuto));
    if (isAuto) {
      setThemeState(getScheduledTheme());
      scheduleSwitch();
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuto, scheduleSwitch]);

  // Apply theme to DOM + persist
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (!isAuto) {
      localStorage.setItem(THEME_KEY, theme);
    }
  }, [theme, isAuto]);

  const setTheme = (newTheme: Theme) => {
    setAutoState(false); // manual override disables auto
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setAutoState(false); // manual toggle disables auto
    setThemeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setAuto = (auto: boolean) => {
    setAutoState(auto);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark: theme === 'dark', isAuto, setAuto }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
